import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabase";
import {
  clearRecoveryState,
  expectsRecovery,
  isRecoveryVerified,
  recoveryLinkExpired,
  recoveryLinkFailed,
  subscribeRecovery,
} from "../lib/passwordRecovery";
import { evaluatePassword, passwordsMatch } from "../lib/passwordPolicy";
import PasswordField from "../components/auth/PasswordField";
import PasswordRules from "../components/auth/PasswordRules";
import { reportError } from "../lib/errorReporter";

// How long the screen waits for Supabase to establish the recovery session
// before it declares the link unusable.
const VERIFY_TIMEOUT_MS = 10000;
const REDIRECT_DELAY_MS = 2500;

function initialPhase() {
  if (isRecoveryVerified()) return "ready";
  if (recoveryLinkFailed()) return "link_error";
  if (expectsRecovery()) return "verifying";
  // No recovery link and no recovery session — an ordinary visitor, including a
  // normally signed-in user, gets no form.
  return "denied";
}

export default function ResetPassword() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [phase, setPhase] = useState(initialPhase);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    if (phase !== "verifying") return undefined;
    if (isRecoveryVerified()) {
      setPhase("ready");
      return undefined;
    }
    const unsubscribe = subscribeRecovery((verified) => {
      if (verified) setPhase("ready");
    });
    const timer = setTimeout(
      () => setPhase((current) => (current === "verifying" ? "link_error" : current)),
      VERIFY_TIMEOUT_MS,
    );
    return () => {
      unsubscribe();
      clearTimeout(timer);
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== "success") return undefined;
    const timer = setTimeout(
      () => nav("/login", { replace: true, state: { passwordChanged: true } }),
      REDIRECT_DELAY_MS,
    );
    return () => clearTimeout(timer);
  }, [phase, nav]);

  const submit = useCallback(
    async (event) => {
      event.preventDefault();
      if (inFlight.current || busy || phase !== "ready") return;
      const { valid } = evaluatePassword(password);
      if (!valid) {
        setError(t("password.error_policy"));
        return;
      }
      if (!passwordsMatch(password, confirmation)) {
        setError(t("password.error_mismatch"));
        return;
      }
      inFlight.current = true;
      setBusy(true);
      setError("");
      // Reached ONLY from the verified recovery state above.
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        inFlight.current = false;
        setBusy(false);
        reportError("auth.password_update");
        if (updateError.code === "same_password") setError(t("password.error_same"));
        else if (updateError.code === "weak_password") setError(t("password.error_policy"));
        else if (updateError.status === 401 || updateError.status === 403) setPhase("link_error");
        else setError(t("password.error_generic"));
        return;
      }
      clearRecoveryState();
      try {
        await supabase.auth.signOut({ scope: "global" });
      } catch (err) {
        reportError("auth.sign_out_after_reset");
      }
      setPassword("");
      setConfirmation("");
      inFlight.current = false;
      setBusy(false);
      setPhase("success");
    },
    [busy, confirmation, password, phase, t],
  );

  const body = () => {
    if (phase === "verifying") {
      return (
        <div className="qw-empty" data-testid="reset-password-verifying">
          {t("password.verifying")}
        </div>
      );
    }

    if (phase === "link_error") {
      return (
        <div data-testid="reset-password-link-error">
          <h1>{t("password.link_invalid_title")}</h1>
          <p className="lead">
            {recoveryLinkExpired()
              ? t("password.link_expired_body")
              : t("password.link_invalid_body")}
          </p>
          <Link
            className="qw-btn primary"
            to="/auth/forgot-password"
            data-testid="reset-password-request-new-link"
          >
            {t("password.request_new_link")}
          </Link>
        </div>
      );
    }

    if (phase === "denied") {
      return (
        <div data-testid="reset-password-denied">
          <h1>{t("password.denied_title")}</h1>
          <p className="lead">{t("password.denied_body")}</p>
          <Link
            className="qw-btn primary"
            to="/auth/forgot-password"
            data-testid="reset-password-request-link"
          >
            {t("password.request_new_link")}
          </Link>
        </div>
      );
    }

    if (phase === "success") {
      return (
        <div data-testid="reset-password-success">
          <h1>{t("password.success_title")}</h1>
          <p className="lead">{t("password.success_body")}</p>
          <Link className="qw-btn primary" to="/login" data-testid="reset-password-go-to-login">
            {t("password.go_to_login")}
          </Link>
        </div>
      );
    }

    return (
      <form onSubmit={submit} noValidate data-testid="reset-password-form">
        <h1>{t("password.reset_title")}</h1>
        <p className="lead">{t("password.reset_lead")}</p>

        <PasswordField
          id="reset-new-password"
          label={t("password.new_password")}
          value={password}
          onChange={(v) => {
            setPassword(v);
            setError("");
          }}
          testId="reset-password-new"
        />
        <PasswordField
          id="reset-confirm-password"
          label={t("password.repeat_password")}
          value={confirmation}
          onChange={(v) => {
            setConfirmation(v);
            setError("");
          }}
          testId="reset-password-confirm"
        />

        <PasswordRules
          value={password}
          confirmation={confirmation}
          showMatch
          testIdPrefix="reset"
        />

        {error && (
          <div className="qw-error" data-testid="reset-password-error">
            {error}
          </div>
        )}

        <button
          className="qw-btn primary stretch"
          style={{ width: "100%", justifyContent: "center", marginTop: 8 }}
          type="submit"
          disabled={busy}
          data-testid="reset-password-submit"
        >
          {busy ? t("password.submitting") : t("password.submit")}
        </button>
      </form>
    );
  };

  return (
    <div className="qw-auth-wrap">
      <div className="qw-auth-side">
        <div>
          <div className="brand">OFFERTRACE</div>
          <div className="head">{t("password.reset_title")}</div>
          <div className="sub">{t("password.reset_side_note")}</div>
        </div>
        <div style={{ opacity: 0.7, fontSize: 12 }}>© OfferTrace</div>
      </div>
      <div className="qw-auth-panel">
        <div className="qw-auth-form" data-testid="reset-password-panel">
          {body()}
        </div>
      </div>
    </div>
  );
}
